import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import SidebarAppointment from '@/components/SidebarAppointment';
import BatteryHistoricalConditionsDialog from '@/features/diagnostics/battery/BatteryHistoricalConditionDialog';
import { EngineOilMfe } from '@/features/microfrontends/EngineOilMfe';
import { HighVoltageBatteryMfe } from '@/features/microfrontends/HighVoltageBatteryMfe';
import { TyresMfe } from '@/features/microfrontends/TyresMfe';
import VehicleWarnings from '@/features/vehicleWarnings/VehicleWarnings';
import { Appointments, Asset, BrakeLinings, Components, FillLevels, Product, TirePressures } from '@/generated/fleetAggregationServiceTypes';
import { FeatureToggle, useFeatureToggle } from '@/hooks/useFeatureToggle';
import { hasFeature } from '@/productsConfig';
import AnalyticsEvents from '@/utils/analytics/AnalyticsEvents';
import SidebarBrakeLinings from './vehicleData/SidebarBrakeLinings';
import { SidebarComponents } from './vehicleData/SidebarComponents';
import { SidebarFillLevels } from './vehicleData/SidebarFillLevels';
import { SidebarTirePressures } from './vehicleData/SidebarTirePressures';

type DiagnosticsSidebarTabsProps = {
    components?: Components;
    brake_linings?: BrakeLinings;
    engine_oil?: FillLevels;
    fill_levels?: FillLevels;
    tire_pressures?: TirePressures;
    appointments: Appointments;
    openCustomComponentDialog: (componentId?: string) => void;
    openDeleteCustomComponentDialog: (componentId: string) => void;
    asset: Asset;
    workshopName?: string;
};

const DiagnosticsSidebarTabs = ({
    components,
    brake_linings,
    engine_oil,
    fill_levels,
    tire_pressures,
    appointments,
    openCustomComponentDialog,
    openDeleteCustomComponentDialog,
    asset,
    workshopName,
}: DiagnosticsSidebarTabsProps) => {
    const [tabIndex, setTabIndex] = useState<number>(0);
    const { featureToggleValue: isServiceCareLAlphaFeatureEnabled } = useFeatureToggle(FeatureToggle.ENABLE_SERVICE_CARE_L_ALPHA);

    return (
        <div>
            <ul className="margin-top-25 margin-bottom-20 nav nav-pills nav-justified">
                <li className={tabIndex === 0 ? 'active' : ''} onClick={setTabIndex.bind(null, 0)}>
                    <span>
                        <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.tab.vehicle_data" />
                    </span>
                </li>
                <li className={`${tabIndex === 1 ? 'active' : ''} ${!appointments.length ? 'disabled' : ''}`} onClick={goToAppointments}>
                    <span>
                        <FormattedMessage id="intl-msg:fleetstatus.global.appointments" /> {`(${appointments.length})`}
                    </span>
                </li>
            </ul>
            <div>
                {tabIndex === 0 && (
                    <>
                        {components && (
                            <>
                                <SidebarComponents
                                    {...components}
                                    openCustomComponentDialog={openCustomComponentDialog}
                                    openDeleteCustomComponentDialog={openDeleteCustomComponentDialog}
                                    asset={asset}
                                    workshopName={workshopName}
                                />
                                {hasFeature('diagnosticDetailsView', asset) && <hr />}
                            </>
                        )}
                        {(brake_linings || tire_pressures) && (
                            <>
                                <h4 className="text-uppercase text-color-dark margin-bottom-25 ">
                                    <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.section.wear_tear" />
                                </h4>
                                <VehicleWarnings asset={asset} />
                                {brake_linings && <SidebarBrakeLinings brakeLinings={brake_linings} asset={asset} />}
                                {tire_pressures &&
                                    (isServiceCareLAlphaFeatureEnabled && asset.product === Product.ServiceCareL ? (
                                        <TyresMfe assetId={asset.id} />
                                    ) : (
                                        <SidebarTirePressures tirePressures={tire_pressures} asset={asset} />
                                    ))}
                                <hr />
                            </>
                        )}
                        {(engine_oil || fill_levels) && (
                            <>
                                <h4 className="text-uppercase text-color-dark margin-bottom-25 ">
                                    <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.section.fill_levels" />
                                </h4>
                                {engine_oil && <EngineOilMfe assetId={asset.id} />}
                                {fill_levels && <SidebarFillLevels fillLevels={fill_levels} asset={asset} />}
                                <hr />
                            </>
                        )}
                        {asset.isElectric && (
                            <>
                                <h4 className="text-uppercase text-color-dark margin-bottom-25 ">
                                    <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.section.battery" />
                                </h4>
                                <HighVoltageBatteryMfe assetId={asset.id} />
                            </>
                        )}
                        {asset.isElectric && <BatteryHistoricalConditionsDialog assetId={asset.id} />}
                    </>
                )}
                {tabIndex === 1 && (
                    <>
                        {appointments.map((appointment) => (
                            <SidebarAppointment key={appointment.date} appointment={appointment} asset={asset} />
                        ))}
                    </>
                )}
            </div>
        </div>
    );

    function goToAppointments() {
        if (appointments.length) {
            AnalyticsEvents.openDiagnosticsSidebarAppointmentsTab(asset.product);

            setTabIndex(1);
        }
    }
};

export default DiagnosticsSidebarTabs;
